import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Iter-274d W5 — On app boot, if `location.hash` contains
 * `#session_id=…` (the Emergent Google SSO return payload), redeem it
 * against `POST /api/auth/google-session`, strip the hash from the URL,
 * and route the user to their intended `next` destination.
 *
 * Mounted once inside the top-level layout so every entry path triggers
 * the check exactly once.
 */
export default function GoogleSessionRedeemer() {
  const { applyGoogleSession, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    // Already logged in? Skip — a stale hash from a bookmark shouldn't
    // clobber the current session.
    if (user) return;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    const sessionId = decodeURIComponent(match[1]);
    // Strip the fragment immediately so a browser back/refresh does not
    // re-attempt with the same (now-consumed) session id.
    try {
      const clean = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", clean);
    } catch { /* noop */ }
    (async () => {
      try {
        await applyGoogleSession(sessionId);
        toast.success("Signed in with Google.");
        let next = "/";
        try {
          const stored = localStorage.getItem("allsale_google_next");
          if (stored) { next = stored; localStorage.removeItem("allsale_google_next"); }
        } catch { /* noop */ }
        nav(next, { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401) toast.error("Google sign-in session expired — please try again.");
        else if (status === 502) toast.error("Google is unreachable right now — please try again in a moment.");
        else if (status === 403) toast.error(typeof detail === "string" ? detail : "Google sign-in is not available in your region.");
        else toast.error(typeof detail === "string" ? detail : "Google sign-in failed.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
